'use client';
import MobileDrawer from '@c/drawers/MobileDrawer';
import Filters from '@c/filters/Filters';
import { useCategoryFilters } from '@util/hooks/useCategoryFilters';
import type { PageSideFilters } from '@util/hooks/useProductsPage';
import type { FacetCounts } from 'models/product';
import { useCallback, useEffect, useState } from 'react';
import { ChevronLeftIcon } from 'lucide-react';
import { CategoryDocument } from 'models/category';

type MobileSearchFiltersProps = {
  showFilters: boolean;
  setShowFilters: (showFilters: boolean) => void;
  filterState: PageSideFilters;
  setFilterState: (filterState: PageSideFilters) => void;
  facet_counts: FacetCounts | undefined;
  mostly_parts?: boolean;
  pageType: string;
  categories: CategoryDocument[];
  initialPage?: 'main' | 'categories';
  /** Replaces the parts filters, for results that are not parts. */
  filters?: React.ReactNode;
};

export default function MobileSearchFilters({
  showFilters,
  setShowFilters,
  filterState,
  setFilterState,
  facet_counts,
  mostly_parts,
  pageType,
  categories,
  initialPage = 'main',
  filters,
}: MobileSearchFiltersProps) {
  const {
    onResetFilters,
    handleFilterChange,
    handleCategoryChange,
    applyPendingFilterChanges,
    pendingFilterChanges,
    handleBikeSelection,
    handleSizeChange,
    handlePriceRangeChange,
    activePage,
    setActivePage,
    onDismiss,
    setPendingFilterChanges,
  } = useCategoryFilters({
    filterState,
    setFilterState,
  });

  const [maxHeight, setMaxHeight] = useState(70);

  useEffect(() => {
    if (showFilters) setActivePage(initialPage);
  }, [showFilters, initialPage, setActivePage]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;

    const maxScroll = 100;
    const minHeight = 70;
    const maxHeight = 90;

    const scrollRatio = Math.min(Math.max(scrollTop / maxScroll, 0), 1);
    const newHeight = minHeight + (maxHeight - minHeight) * scrollRatio;

    setMaxHeight(newHeight);
  }, []);

  return (
    <MobileDrawer
      show={showFilters}
      dismiss={() => {
        setShowFilters(false);
        onDismiss();
      }}
      title={
        filters
          ? 'Filter'
          : activePage === 'main'
          ? 'Filter'
          : activePage === 'shipsTo'
          ? 'Ships To'
          : activePage === 'shipsFrom'
          ? 'Ships From'
          : activePage === 'bootSizes'
          ? 'Boot Size'
          : activePage === 'pantSizes'
          ? 'Pant Size'
          : activePage === 'letterSizes'
          ? 'Size'
          : activePage === 'rideTypes'
          ? 'Ride Types'
          : activePage.charAt(0).toUpperCase() + activePage.slice(1)
      }
      maxHeight={maxHeight}
      headerClassName="px-[4rem]"
      headerExtraButton={
        <>
          {filters || activePage === 'main' ? (
            <button
              type="button"
              onClick={() => {
                onResetFilters();
              }}
              className="text-[2.1rem] font-medium text-brand-secondary"
            >
              Clear
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setActivePage('main');
              }}
              className="flex items-center gap-2 text-[2.1rem] font-medium text-brand-secondary"
            >
              <ChevronLeftIcon className="h-10 w-10 text-brand-secondary" />
              Back
            </button>
          )}
        </>
      }
      onScroll={handleScroll}
    >
      <div className="min-h-[60rem] px-8 pb-[10rem] pt-8">
        {filters ?? (
        <Filters
          filterState={{ ...filterState, ...pendingFilterChanges }}
          handleFilterChange={handleFilterChange}
          handlePriceRangeChange={handlePriceRangeChange}
          handleCategoryChange={handleCategoryChange}
          categories={categories}
          facet_counts={facet_counts}
          pendingFilterChanges={pendingFilterChanges}
          setPendingFilterChanges={setPendingFilterChanges}
          mostly_parts={mostly_parts}
          handleBikeSelection={handleBikeSelection}
          handleSizeChange={handleSizeChange}
          pageType={pageType}
          activePage={activePage}
          setActivePage={setActivePage}
          dismissFilterDrawer={() => setShowFilters(false)}
        />
        )}
      </div>

      <div className="absolute bottom-0 w-full bg-white p-6">
        <button
          type="button"
          className="w-full rounded-[2rem] bg-brand-secondary p-6 text-[2.1rem] font-semibold text-white"
          onClick={() => {
            applyPendingFilterChanges();
            setShowFilters(false);
            setActivePage('main');
          }}
        >
          Show results
        </button>
      </div>
    </MobileDrawer>
  );
}
